import React                from "react";
import PropTypes            from "prop-types";
import Store                from "Dashboard/Core/Store";
import useForm              from "Dashboard/Hooks/Form";
import useDialog            from "Dashboard/Hooks/Dialog";
import Commons              from "Utils/Commons";

// Dashboard
import EditDialog           from "Dashboard/Components/Dialogs/EditDialog";
import Columns              from "Dashboard/Components/Form/Columns";
import InputField           from "Dashboard/Components/Form/InputField";
import InputItem            from "Dashboard/Components/Form/InputItem";
import TabList              from "Dashboard/Components/Tab/TabList";
import TabItem              from "Dashboard/Components/Tab/TabItem";



/**
 * The Reserve Type Edit Dialog
 * @param {Object} props
 * @returns {React.ReactElement}
 */
function ReserveTypeEdit(props) {
    const { open, elemID, onClose, onSubmit } = props;

    const { activities, notificationTypes, courts, visitTypes, statuses } = Store.useState("reserveType");
    const { editElem } = Store.useAction("reserveType");


    // The Fields
    const fields = {
        general : [ "name", "activityID", "notificationTypeID", "courtIDs", "visitTypeID", "status", "position" ],
        match   : [ "minSingles", "maxSingles", "conceptSingles", "minDoubles", "maxDoubles", "conceptDoubles" ],
        periods : [ "periods" ],
        options : [ "maxDailyReserves", "maxActiveReserves", "daysAnticipation", "minsBeforeEdit" ],
    };

    // The Current State
    const [ tab, setTab ] = React.useState("general");

    // The Initial Data
    const initialData = {
        reserveTypeID      : 0,
        activityID         : 0,
        notificationTypeID : 0,
        name               : "",
        courtIDs           : [],
        visitTypeID        : 0,
        visitTypeName      : "",
        periods            : "[{}]",
        allowsDoubles      : 0,
        minSingles         : 0,
        maxSingles         : 2,
        conceptSingles     : "",
        minDoubles         : 0,
        maxDoubles         : 4,
        conceptDoubles     : "",
        maxDailyReserves   : 0,
        maxActiveReserves  : 0,
        daysAnticipation   : 0,
        minsBeforeEdit     : 0,
        reserveMins        : 0,
        showDetailView     : 0,
        showDetailMembers  : 0,
        status             : "Active",
        position           : 0,
    };

    // Handles the Set
    const handleSet = (elem) => {
        setElem({
            ...elem,
            periods : elem?.periods?.length ? JSON.stringify(elem.periods) : "[{}]",
        });
        setTab("general");
    };

    // The Form State
    const {
        data, errors, setElem, handleChange, handleSubmit,
    } = useForm("reserveType", initialData, editElem, onSubmit);

    // Load the Data
    const { loading } = useDialog("reserveType", open, elemID, null, handleSet);

    // Returns the Error Count in the Step
    const getErrorCount = (tab) => {
        return Commons.getErrorCount(fields, errors, tab);
    };



    // Do the Render
    return <EditDialog
        open={open}
        icon="reserve-type"
        title={elemID ? "RESERVE_TYPES_EDIT_TITLE" : "RESERVE_TYPES_CREATE_TITLE"}
        error={errors.form}
        onSubmit={handleSubmit}
        onClose={onClose}
        isLoading={loading}
    >
        <TabList selected={tab} onClick={setTab} inDialog>
            <TabItem message="GENERAL_GENERAL"     value="general" badge={getErrorCount("general")} />
            <TabItem message="RESERVE_TYPES_MATCH" value="match"   badge={getErrorCount("match")}   />
            <TabItem message="GENERAL_PERIODS"     value="periods" badge={getErrorCount("periods")} />
            <TabItem message="GENERAL_OPTIONS"     value="options" badge={getErrorCount("options")} />
        </TabList>

        <Columns isHidden={tab !== "general"}>
            <InputField
                className="columns-double"
                name="name"
                label="GENERAL_NAME"
                value={data.name}
                error={errors.name}
                onChange={handleChange}
                isRequired
            />

            <InputField
                type="select"
                name="activityID"
                label="ACTIVITIES_SINGULAR"
                options={activities}
                value={data.activityID}
                error={errors.activityID}
                onChange={handleChange}
                isRequired
            />
            <InputField
                type="select"
                name="notificationTypeID"
                label="NOTIFICATION_TYPES_SINGULAR"
                options={notificationTypes[data.activityID] || []}
                value={data.notificationTypeID}
                error={errors.notificationTypeID}
                onChange={handleChange}
                isRequired
            />

            <InputField
                className="columns-double"
                type="chooser"
                name="courtIDs"
                label="COURTS_NAME"
                options={courts[data.activityID] || []}
                value={data.courtIDs}
                error={errors.courtIDs}
                onChange={handleChange}
                isRequired
            />
            <InputField
                className="columns-double"
                type="select"
                name="visitTypeID"
                label="RESERVE_TYPES_VISIT_TYPE"
                options={visitTypes}
                value={data.visitTypeID}
                error={errors.visitTypeID}
                onChange={handleChange}
                isRequired
            />

            <InputField
                type="select"
                name="status"
                label="GENERAL_STATUS"
                options={statuses}
                value={data.status}
                error={errors.status}
                onChange={handleChange}
                hideClear
                isRequired
            />
            <InputField
                type="number"
                name="position"
                label="GENERAL_POSITION"
                value={data.position}
                error={errors.position}
                onChange={handleChange}
                minValue={0}
            />
        </Columns>

        <InputField
            isHidden={tab !== "match"}
            type="toggle"
            name="allowsDoubles"
            label="RESERVE_TYPES_ALLOWS_DOUBLES"
            value={data.allowsDoubles}
            error={errors.allowsDoubles}
            onChange={handleChange}
            withBorder
        />
        <Columns isHidden={tab !== "match"} amount={data.allowsDoubles ? 2 : 1}>
            <InputField
                type="number"
                name="minSingles"
                label="RESERVE_TYPES_MIN_SINGLES"
                value={data.minSingles}
                error={errors.minSingles}
                onChange={handleChange}
                minValue={0}
                step={1}
            />
            <InputField
                isHidden={!data.allowsDoubles}
                type="number"
                name="minDoubles"
                label="RESERVE_TYPES_MIN_DOUBLES"
                value={data.minDoubles}
                error={errors.minDoubles}
                onChange={handleChange}
                minValue={0}
                step={1}
            />

            <InputField
                type="number"
                name="maxSingles"
                label="RESERVE_TYPES_MAX_SINGLES"
                value={data.maxSingles}
                error={errors.maxSingles}
                onChange={handleChange}
                minValue={0}
                step={1}
                isRequired
            />
            <InputField
                isHidden={!data.allowsDoubles}
                type="number"
                name="maxDoubles"
                label="RESERVE_TYPES_MAX_DOUBLES"
                value={data.maxDoubles}
                error={errors.maxDoubles}
                onChange={handleChange}
                minValue={0}
                step={1}
                isRequired
            />

            <InputField
                name="conceptSingles"
                label="RESERVE_TYPES_CONCEPT_SINGLES"
                value={data.conceptSingles}
                error={errors.conceptSingles}
                onChange={handleChange}
                isRequired
            />
            <InputField
                isHidden={!data.allowsDoubles}
                name="conceptDoubles"
                label="RESERVE_TYPES_CONCEPT_DOUBLES"
                value={data.conceptDoubles}
                error={errors.conceptDoubles}
                onChange={handleChange}
                isRequired
            />
        </Columns>

        <InputField
            isHidden={tab !== "periods"}
            type="fields"
            name="periods"
            title="GENERAL_PERIOD_TITLE"
            addButton="GENERAL_ADD_PERIOD"
            value={data.periods}
            error={errors.periods}
            errors={errors}
            onChange={handleChange}
            columns="2"
            isSortable
        >
            <InputItem
                type="chooser"
                name="days"
                label="GENERAL_DAYS"
                options="DATE_DAY_NAMES_MONDAY"
                columns="2"
                isRequired
            />
            <InputItem
                type="time"
                name="from"
                label="GENERAL_FROM"
                isRequired
            />
            <InputItem
                type="time"
                name="to"
                label="GENERAL_TO"
                isRequired
            />
            <InputItem
                type="number"
                name="singlesDuration"
                label="RESERVE_TYPES_SINGLES_DURATION"
                columns={data.allowsDoubles ? "1" : "2"}
                isRequired
            />
            <InputItem
                isHidden={!data.allowsDoubles}
                type="number"
                name="doublesDuration"
                label="RESERVE_TYPES_DOUBLES_DURATION"
                isRequired
            />
        </InputField>

        <Columns isHidden={tab !== "options"}>
            <InputField
                type="number"
                name="maxDailyReserves"
                label="RESERVE_TYPES_MAX_DAILY_RESERVES"
                value={data.maxDailyReserves}
                error={errors.maxDailyReserves}
                onChange={handleChange}
                minValue={0}
                step={1}
                isRequired
            />
            <InputField
                type="number"
                name="maxActiveReserves"
                label="RESERVE_TYPES_MAX_ACTIVE_RESERVES"
                value={data.maxActiveReserves}
                error={errors.maxActiveReserves}
                onChange={handleChange}
                minValue={0}
                step={1}
                isRequired
            />
            <InputField
                type="number"
                name="daysAnticipation"
                label="RESERVE_TYPES_DAYS_ANTICIPATION"
                value={data.daysAnticipation}
                error={errors.daysAnticipation}
                onChange={handleChange}
                minValue={0}
                step={1}
                isRequired
            />
            <InputField
                type="number"
                name="minsBeforeEdit"
                label="RESERVE_TYPES_MINS_BEFORE_EDIT"
                value={data.minsBeforeEdit}
                error={errors.minsBeforeEdit}
                onChange={handleChange}
                minValue={0}
                step={1}
                isRequired
            />
            <InputField
                className="columns-double"
                type="number"
                name="reserveMins"
                label="RESERVE_TYPES_RESERVE_MINS"
                value={data.reserveMins}
                error={errors.reserveMins}
                onChange={handleChange}
                minValue={0}
                step={1}
                isRequired
            />

            <InputField
                type="toggle"
                name="showDetailView"
                label="RESERVE_TYPES_SHOW_DETAIL_VIEW"
                value={data.showDetailView}
                error={errors.showDetailView}
                onChange={handleChange}
                withBorder
            />
            <InputField
                type="toggle"
                name="showDetailMembers"
                label="RESERVE_TYPES_SHOW_DETAIL_MEMBERS"
                value={data.showDetailMembers}
                error={errors.showDetailMembers}
                onChange={handleChange}
                withBorder
            />
        </Columns>
    </EditDialog>;
}

/**
 * The Property Types
 * @typedef {Object} propTypes
 */
ReserveTypeEdit.propTypes = {
    open     : PropTypes.bool.isRequired,
    onClose  : PropTypes.func.isRequired,
    onSubmit : PropTypes.func.isRequired,
    elemID   : PropTypes.number,
};

export default ReserveTypeEdit;
